/**
 * Turn a v3 map layer's resolved `style` plus one feature into draw values.
 *
 * The engine resolves every presentation decision (engine
 * docs/map-document-v3-contract.md), so these are lookups into the document:
 * a categorical class's color, a sequential band's color from `breaks`, a
 * single style's color, the no_data fill. The one calculation left is the
 * proportional marker radius, whose rule the contract states for the client
 * (see proportionalRadius()).
 *
 * Every function here is pure and assumes a layer that already passed
 * normalizeMapDocument() (utils/mapDocument.js).
 */

/** Stroke weight when a choropleth style sends no `stroke` (client default). */
const DEFAULT_STROKE_WEIGHT = 1;

/** The feature's raw value for the layer's value_field (undefined if none). */
export function featureValue(layer, feature) {
    const field = layer.style.value_field;
    return field ? feature.properties?.[field] : undefined;
}

/**
 * Where a style keeps its ramp/breaks/classes/radii: on the style itself for
 * a choropleth, nested under `marker` for every other render type (contract:
 * "Points/proportional/lines/routes layers carry a `marker` object").
 * Shared with mapDocument.js's contract checks, so the rule lives in one place.
 */
export function styleParts(style, renderType) {
    return renderType === 'choropleth' ? style : style.marker || {};
}

function isMissing(value) {
    return value === null || value === undefined || value === '';
}

/**
 * A feature value as the text the engine used for categorical class values.
 *
 * WHY: the engine builds each class `value` with Python's str()
 * (build_categorical_classes in engine/tools/map_payload.py). For strings that
 * is the same as JavaScript's String(), but booleans differ: Python writes
 * True/False, JavaScript writes true/false. Without this, a boolean category
 * (e.g. an "is open" field) would match none of its classes.
 */
export function engineString(value) {
    if (value === true) return 'True';
    if (value === false) return 'False';
    return String(value);
}

/** The color of the categorical class matching the feature's value, or null. */
export function categoricalColor(layer, value) {
    if (isMissing(value)) {
        return null;
    }
    const classes = styleParts(layer.style, layer.render_type).classes || [];
    const text = engineString(value);
    const match = classes.find((c) => c.value === text);
    return match ? match.color : null;
}

/**
 * The color of the sequential band a value falls in, looked up from the
 * engine's `breaks` (contract: "ramp[i] draws values in [breaks[i],
 * breaks[i+1]), last band inclusive of the domain max").
 *
 * A degenerate layer (every value identical) arrives as a one-color ramp with
 * breaks [v, v]; v lands in that single band through the inclusive last band.
 * Returns null for a non-numeric value or one outside the breaks, which the
 * document gives no color for (the caller falls back to no_data).
 */
export function bandColor(value, breaks, ramp) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
    }
    const last = ramp.length - 1;
    if (value < breaks[0] || value > breaks[last + 1]) {
        return null;
    }
    for (let i = 0; i < last; i += 1) {
        if (value < breaks[i + 1]) {
            return ramp[i];
        }
    }
    return ramp[last];
}

/**
 * A proportional marker's radius for a value.
 *
 * The contract states this rule for the client: min_radius plus
 * (max_radius - min_radius) scaled by the SQUARE ROOT of the value's
 * fractional position in `domain`, so a circle's area (its visual weight)
 * grows with the value. Values outside the domain are clamped. Non-numeric
 * values return null. A degenerate domain never reaches here: the engine sends
 * a flat `radius` instead of min_radius/max_radius for it.
 */
export function proportionalRadius(value, domain, marker) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
    }
    const [min, max] = domain;
    if (max <= min) {
        return marker.min_radius;
    }
    const ratio = Math.min(1, Math.max(0, (value - min) / (max - min)));
    return (
        marker.min_radius +
        (marker.max_radius - marker.min_radius) * Math.sqrt(ratio)
    );
}

/**
 * Fill and stroke for one choropleth feature, or null if the document gives
 * no color for it (no class/band match and no no_data fill).
 */
export function polygonStyle(layer, feature) {
    const { style } = layer;
    let fill = null;
    if (style.kind === 'single') {
        fill = style.ramp[0];
    } else if (style.kind === 'categorical') {
        fill = categoricalColor(layer, featureValue(layer, feature));
    } else {
        fill = bandColor(
            featureValue(layer, feature),
            style.breaks,
            style.ramp
        );
    }
    fill = fill || style.no_data?.fill || null;
    if (!fill) {
        return null;
    }
    return {
        fillColor: fill,
        strokeColor: style.stroke?.color || fill,
        strokeWeight: style.stroke?.weight ?? DEFAULT_STROKE_WEIGHT,
    };
}

/**
 * Color and radius for one point/proportional feature, or null if the
 * document gives no color for it.
 */
export function markerStyle(layer, feature) {
    const { style } = layer;
    const marker = style.marker;
    const noDataFill = style.no_data?.fill || null;

    if (style.kind === 'single') {
        return { fillColor: marker.color, radius: marker.radius };
    }
    if (style.kind === 'categorical') {
        const color =
            categoricalColor(layer, featureValue(layer, feature)) || noDataFill;
        return color ? { fillColor: color, radius: marker.radius } : null;
    }

    const value = featureValue(layer, feature);
    const color = bandColor(value, marker.breaks, marker.ramp);
    const hasRange =
        Number.isFinite(marker.min_radius) &&
        Number.isFinite(marker.max_radius);
    const radius = hasRange
        ? proportionalRadius(value, style.domain, marker)
        : marker.radius;
    if (color && radius !== null) {
        return { fillColor: color, radius };
    }
    if (noDataFill) {
        // Same radius the engine gives the legend's "No data" entry.
        return {
            fillColor: noDataFill,
            radius: hasRange ? marker.min_radius : marker.radius,
        };
    }
    return null;
}
